import { Component, computed, EventEmitter, inject, Input, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, ValidationErrors, Validators } from '@angular/forms';
import { ChangePassword } from '@app/core/models/change-password.model';
import { UiFeedbackService } from '@app/core/services/ui-feedback.service';
import { passwordValidator } from '@app/core/utils/password-validations';

import { passwordRegex } from '@app/core/utils/patterns';

@Component({
  selector: 'motor-calculo-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss']
})
export class ResetPasswordComponent {
  @Output() formValue = new EventEmitter<ChangePassword>();
  @Output() cancelButton = new EventEmitter<void>();


  @Input() closable = false;
  @Input()
  set showOldPasswordInput(value: boolean) {
    this.#showOldPasswordInput(value);
  }

  @Input() showInstructions = true;
  @Input()
  set initialOldPassword(value: string) {
    if (value) {
      this.oldPassword.setValue(value);
    }
  }


  readonly #formBuilder = inject(FormBuilder);
  readonly #uiFeedback = inject(UiFeedbackService);

  resetPassword = this.#formBuilder.group({
    oldPassword: [''],
    password: ['', [Validators.required, Validators.pattern(passwordRegex), passwordValidator]],
    confirmPassword: ['', [Validators.required]]
  });

  isOldPasswordInputVisible = false;
  showOldPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;

  cancelButtonText = computed(() => this.#uiFeedback.showResetPasswordModal().cancelButtonText)

  get invalidForm(): boolean {
    return this.resetPassword.invalid;
  }

  get oldPassword(): FormControl<string | null> {
    return this.resetPassword.controls['oldPassword'];
  }

  get password(): FormControl<string | null> {
    return this.resetPassword.controls['password'];
  }

  get confirmPassword(): FormControl<string | null> {
    return this.resetPassword.controls['confirmPassword'];
  }

  get confirPasswordIsDirtyAndTouched(): boolean {
    return this.confirmPassword.dirty && this.confirmPassword.touched;
  }


  constructor() {
    this.resetPassword.controls['confirmPassword'].setValidators([Validators.required, this.#passwordMatchValidator.bind(this)]);
  }


  onSubmit(): void {
    if (this.invalidForm) return;
    this.formValue.emit({
      password: this.password.value!,
      oldPassword: this.oldPassword.value!
    })
  }

  resetModal() {
    this.resetPassword.reset();
    this.#uiFeedback.configurateResetPasswordModal({ visible: false, showCancelButton: false });
    this.cancelButton.emit();
  }


  #passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const password = this.password.value;
    const confirmPassword = control.value;

    if (password !== confirmPassword) return { mismatch: true };
    return null;
  }

  #showOldPasswordInput(value: boolean) {
    this.isOldPasswordInputVisible = value;
    if (value) {
      this.resetPassword.addControl('oldPassword', new FormControl<string>('', [Validators.required]));
    } else {
      this.resetPassword.controls['oldPassword'].removeValidators(Validators.required);
    }
  }
}
